import {
  CanvasTexture,
  Matrix3,
  MeshBasicMaterial,
  NearestFilter,
  sRGBEncoding,
  StaticReadUsage,
  Vector2,
} from 'three';
import { KongMaterial } from '../../Materials/Kong/material';

function mergeMaterials(object, customUniforms, handleEmissive = true, handleShadow = true) {
  const bufferSide = { x: 1024, y: 512 };
  const bufferCanvas = document.createElement('canvas');
  const bufferEmissiveCanvas = document.createElement('canvas');

  bufferCanvas.width = bufferSide.x;
  bufferCanvas.height = bufferSide.y;
  bufferEmissiveCanvas.width = bufferSide.x;
  bufferEmissiveCanvas.height = bufferSide.y;

  const bufferEmissiveCtx = bufferEmissiveCanvas.getContext('2d');

  const bufferEmissiveCanvasTexture = new CanvasTexture(bufferEmissiveCanvas);

  bufferEmissiveCanvasTexture.flipY = false;
  bufferEmissiveCanvasTexture.magFilter = NearestFilter;
  bufferEmissiveCanvasTexture.encoding = sRGBEncoding;

  const coords = new Vector2(0, 0);
  let maxHeight = 0;
  const mat3 = new Matrix3();

  object.traverse((o) => {
    if (o.material?.map) {
      const position = o.geometry.getAttribute('position');
      const normal = o.geometry.getAttribute('normal');
      const uv = o.geometry.getAttribute('uv');

      if (this.app.webgl.renderer.capabilities.isWebGL2) {
        position.setUsage(StaticReadUsage);
        normal.setUsage(StaticReadUsage);
        uv.setUsage(StaticReadUsage);
      }

      const uv3 = uv.clone();

      // o.geometry = new BufferGeometry();

      o.geometry.setAttribute('position', position);
      o.geometry.setAttribute('normal', normal);
      o.geometry.setAttribute('uv', uv);
      o.geometry.setAttribute('aCustomUv', uv3);

      if (coords.x + o.material.map.image.width > bufferSide.x) {
        coords.x = 0;
        coords.y += maxHeight;
      }

      if (o.material.emissiveMap && handleEmissive) {
        bufferEmissiveCtx.drawImage(
          o.material.emissiveMap.image,
          coords.x,
          coords.y,
          o.material.map.image.width,
          o.material.map.image.height
        );

        maxHeight = Math.max(maxHeight, o.material.map.image.height);

        /* eslint-disable */
        mat3.set(
          o.material.map.image.width / bufferSide.x,
          0,
          coords.x / bufferSide.x,
          0,
          o.material.map.image.height / bufferSide.y,
          coords.y / bufferSide.y,
          0,
          0,
          1
        );
        /* eslint-enable */

        coords.x += o.material.map.image.width;
        uv3.applyMatrix3(mat3);
      }

      o.geometry.setAttribute('uv2', uv3.clone());

      o.material.map.magFilter = o.material.map.minFilter = NearestFilter;

      const uuid = o.material.uuid;
      if (this.instanciedMaterials.has(uuid)) {
        o.material = this.instanciedMaterials.get(uuid);
      } else {
        o.material = new KongMaterial({
          uniforms: {
            ...customUniforms,
            tShadowMap: { value: null },
            tNoise: { value: this.app.tools.noise.texture },
            tMap: { value: o.material.map },
            tEmissiveMap: {
              value: handleEmissive ? bufferEmissiveCanvasTexture : o.material.emissiveMap,
            },
            uMapSize: { value: o.material.map.image.width },
            uVisiblity2: { value: 1 },
            uUseShadowMap: { value: handleShadow ? 1 : 0 },
          },
        });

        // o.material = new MeshBasicMaterial({ map: o.material.map, color: 0x555555 });
        this.instanciedMaterials.set(uuid, o.material);
      }
    }
  });
}

function computeAo(model) {
  const { texture } = this.app.tools.staticLightComputing.compute(model);
  texture.flipY = false;
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;

  model.traverse((o) => {
    if (o.material) {
      o.material.uniforms.tShadowMap.value = texture;
    }
  });

  // *** DEBUG AO *** //
  // const debug = new Mesh(new PlaneGeometry(80, 80), new MeshBasicMaterial({ map: texture, side: 2 }));
  // debug.position.set(50, 80, 0);
  // this.add(debug);

  // const debug2 = new Mesh(new PlaneGeometry(40, 40), new MeshBasicMaterial({ map: this.bufferEmissiveCanvasTexture, side: 2, blending: AdditiveBlending }));
  // debug2.position.set(50, 80, 0.1);
  // this.add(debug2);
  // *** DEBUG AO *** //
}

function afterFirstRender() {
  this.firstRenderDone = true;
  this.model.traverse((o) => {
    if (o.geometry) {
      o.geometry.getAttribute('position')?.setUsage(StaticReadUsage);
      o.geometry.getAttribute('normal')?.setUsage(StaticReadUsage);
      o.geometry.getAttribute('uv')?.setUsage(StaticReadUsage);
      o.geometry.getAttribute('uv2')?.setUsage(StaticReadUsage);
      o.geometry.getAttribute('aCustomUv')?.setUsage(StaticReadUsage);
    }
  });
}

export { mergeMaterials, computeAo, afterFirstRender };
